<div class="theme-editor">
  <!-- Header -->
  <div class="editor-header">
    <div class="header-left">
      <button class="back-btn" (click)="onCancel()" title="Back to Themes">
        <svg viewBox="0 0 24 24" class="icon">
          <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
        </svg>
      </button>
      <div class="title-section">
        <h1>{{ isEditMode ? 'Edit Theme' : 'Create Theme' }}</h1>
        <p>{{ isEditMode ? 'Modify theme properties and styling' : 'Design a new email theme with custom variables' }}</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="cancel-btn" (click)="onCancel()" [disabled]="saving">Cancel</button>
      <button class="save-btn" (click)="onSave()" [disabled]="themeForm.invalid || saving">
        <svg viewBox="0 0 24 24" class="icon" *ngIf="!saving">
          <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
        </svg>
        <div class="spinner" *ngIf="saving"></div>
        {{ saving ? 'Saving...' : 'Save Theme' }}
      </button>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-overlay" *ngIf="loading">
    <div class="spinner"></div>
    <p>Loading theme...</p>
  </div>

  <!-- Form Content -->
  <div class="editor-content" *ngIf="!loading">
    <form [formGroup]="themeForm">
      <div class="form-grid">
        <!-- Basic Information -->
        <div class="form-section">
          <h3 class="section-title">Basic Information</h3>
          <div class="form-row">
            <div class="form-field">
              <label for="themeName">Theme Name *</label>
              <input id="themeName" type="text" formControlName="name" placeholder="Enter theme name..." [class.error]="isFieldInvalid('name')" (blur)="onGenerateSlug()" />
              <div class="field-error" *ngIf="isFieldInvalid('name')">
                {{ getFieldError('name') }}
              </div>
            </div>
            <div class="form-field">
              <label for="themeSlug">Slug</label>
              <input id="themeSlug" type="text" formControlName="slug" placeholder="Auto-generated from name" [class.error]="isFieldInvalid('slug')" />
              <small class="field-hint">URL-friendly identifier (auto-generated if empty)</small>
            </div>
          </div>

          <div class="form-field">
            <label for="themeDescription">Description</label>
            <textarea id="themeDescription" formControlName="description" placeholder="Describe this theme..." rows="3" [class.error]="isFieldInvalid('description')"></textarea>
            <div class="field-error" *ngIf="isFieldInvalid('description')">
              {{ getFieldError('description') }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-field checkbox-field">
              <label class="checkbox-label">
                <input type="checkbox" formControlName="is_active" />
                <span class="checkmark"></span>
                Active Theme
              </label>
              <small class="field-hint">Inactive themes won't be available for selection</small>
            </div>
            <div class="form-field checkbox-field">
              <label class="checkbox-label">
                <input type="checkbox" formControlName="is_default" />
                <span class="checkmark"></span>
                Default Theme
              </label>
              <small class="field-hint">This will be the default theme for new templates</small>
            </div>
          </div>
        </div>

        <!-- CSS Properties -->
        <div class="form-section">
          <h3 class="section-title">Theme Variables</h3>
          <div class="css-properties" formGroupName="global_css">
            <div class="property-grid">
              <div class="property-field" *ngFor="let property of commonCssProperties" [class.color-field]="property.type === 'color'">
                <label [for]="'css_' + property.key">{{ property.label }}</label>
                <div class="input-wrapper">
                  <!-- Color input -->
                  @if (property.type === 'color') {
                  <input [id]="'css_' + property.key" type="color" [formControlName]="property.key" [placeholder]="property.defaultValue" />
                  }
                  <!-- Select input -->
                  @if (property.type === 'select') {
                  <select [id]="'css_' + property.key" [formControlName]="property.key">
                    <option *ngFor="let option of property.options" [value]="option">{{ option }}</option>
                  </select>
                  }
                  <!-- Text input -->
                  @if (property.type === 'text') {
                  <input [id]="'css_' + property.key" type="text" [formControlName]="property.key" [placeholder]="property.defaultValue" />
                  }
                  <span class="property-key">{{ property.key }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview -->
        <div class="form-section">
          <h3 class="section-title">Preview</h3>
          <div class="theme-preview">
            <div class="preview-header">
              <h4>Email Preview</h4>
              <div class="color-swatches">
                <div class="color-swatch" [style.background-color]="themeForm.get('global_css.primary_color')?.value" title="Primary Color"></div>
                <div class="color-swatch" [style.background-color]="themeForm.get('global_css.secondary_color')?.value" title="Secondary Color"></div>
                <div class="color-swatch" [style.background-color]="themeForm.get('global_css.text_color')?.value" title="Text Color"></div>
                <div class="color-swatch" [style.background-color]="themeForm.get('global_css.background_color')?.value" title="Background Color"></div>
              </div>
            </div>
            <div class="preview-content">
              <div class="preview-gradient" [style.background]="getPreviewGradient()"></div>
              <div
                class="sample-email"
                [style.font-family]="themeForm.get('global_css.font_family')?.value"
                [style.font-size]="themeForm.get('global_css.font_size')?.value"
                [style.line-height]="themeForm.get('global_css.line_height')?.value"
                [style.color]="themeForm.get('global_css.text_color')?.value"
                [style.background-color]="themeForm.get('global_css.background_color')?.value"
                [style.border-radius]="themeForm.get('global_css.border_radius')?.value"
                [style.padding]="themeForm.get('global_css.padding')?.value"
                [style.max-width]="themeForm.get('global_css.max_width')?.value"
                [style.text-align]="themeForm.get('global_css.text_alignment')?.value"
              >
                <h2 [style.color]="themeForm.get('global_css.primary_color')?.value">Sample Email Title</h2>
                <p>This is how your email content will look with this theme applied. The colors, fonts, and spacing will be consistent across all your email templates.</p>
                <button [style.background-color]="themeForm.get('global_css.primary_color')?.value" [style.border-radius]="themeForm.get('global_css.border_radius')?.value">
                  Call to Action
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>
